import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type AdminGchatCustomRow } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// Connected-customer table — only rows where the per-brain
// /gchat-custom/install endpoint returned connected=true.
const connectedColumns: ColumnDef<AdminGchatCustomRow>[] = [
  {
    accessorKey: "customerName",
    header: "Workspace",
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.original.customerName ?? "—"}</span>
        {row.original.customerId && (
          <span className="text-xs text-muted-foreground font-mono">{row.original.customerId}</span>
        )}
      </div>
    ),
  },
  {
    accessorKey: "name",
    header: "Container",
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => row.original.organizationName ?? row.original.organizationId,
  },
  {
    id: "open",
    header: "",
    cell: ({ row }) => (
      <Button asChild variant="ghost" size="sm">
        <Link
          to="/orgs/$orgId/containers/$containerId/chat"
          params={{ orgId: row.original.organizationId, containerId: row.original.containerId }}
        >
          <ExternalLink className="size-3.5" />
          Open
        </Link>
      </Button>
    ),
  },
];

// Errored rows — surface so we know which brains are unreachable instead
// of silently filtering them out.
const erroredColumns: ColumnDef<AdminGchatCustomRow>[] = [
  {
    accessorKey: "name",
    header: "Container",
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => row.original.organizationName ?? row.original.organizationId,
  },
  {
    accessorKey: "error",
    header: "Error",
    cell: ({ row }) => (
      <span className="text-xs text-destructive">{row.original.error}</span>
    ),
  },
];

export function AdminGchatCustomPage() {
  useDocumentTitle("Admin: Google Chat");
  // Live fan-out across every running brain — runs N requests server-side
  // (POOL_SIZE=8 there). Cache for 60s so a refresh doesn't re-hammer the
  // fleet; admin clicks "Refresh" via refetch button to bypass.
  const query = useQuery({
    queryKey: ["admin-gchat-custom-installed"],
    queryFn: api.admin.gchatCustomInstalled,
    staleTime: 60_000,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Custom Google Chat"
          subtitle="Customers using their own Chat app (vs. distributed Boo Chat)."
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          {query.isFetching ? "Scanning…" : "Refresh"}
        </Button>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError || !query.data ? (
        <ErrorComponent error={query.error ?? "Failed to load"} />
      ) : (
        <div className="space-y-8">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Scanned <strong className="text-foreground">{query.data.scanned}</strong> of{" "}
              <strong className="text-foreground">{query.data.total}</strong> brains
            </span>
            <span>·</span>
            <span>
              <strong className="text-foreground">{query.data.connected.length}</strong> connected
            </span>
            {query.data.errored.length > 0 && (
              <>
                <span>·</span>
                <span>
                  <strong className="text-destructive">{query.data.errored.length}</strong> errored
                </span>
              </>
            )}
          </div>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Connected</h2>
              <Badge variant="secondary">{query.data.connected.length}</Badge>
            </div>
            {query.data.connected.length > 0 ? (
              <DataTable columns={connectedColumns} data={query.data.connected} />
            ) : (
              <p className="text-sm text-muted-foreground">No customers connected via custom Google Chat.</p>
            )}
          </section>

          {query.data.errored.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">Errored</h2>
                <Badge variant="destructive">{query.data.errored.length}</Badge>
              </div>
              <DataTable columns={erroredColumns} data={query.data.errored} />
            </section>
          )}
        </div>
      )}
    </div>
  );
}
